(() => {
  const MANIFEST_VIEW = 'stock-priority';

  class StockPriorityCollection {
    constructor() {
      this.responseCache = new Map();
      this.renderVersion = 0;
      this.render = this.render.bind(this);
    }

    init() {
      document.addEventListener('collection:product-grid-rendered', this.render);
      this.render();
    }

    async render() {
      const version = ++this.renderVersion;
      const grid = document.querySelector('ul#product-grid');
      const currentUrl = new URL(window.location.href);

      if (!grid) return;
      const balanceRecommended = grid.dataset.balanceRecommended === 'true' && grid.dataset.sortBy === 'manual';
      const prioritizeAvailable = grid.dataset.stockPriority === 'true';
      if (!balanceRecommended && (!prioritizeAvailable || currentUrl.searchParams.has('filter.v.availability'))) return;

      const pageSize = Number(grid.dataset.pageSize);
      const currentPage = Number(grid.dataset.currentPage);
      const totalItems = Number(grid.dataset.totalItems);
      const sectionId = grid.dataset.id;

      if (!pageSize || !currentPage || !totalItems || !sectionId) return;

      const collection = grid.closest('.collection');
      collection?.classList.add('loading', 'stock-priority-loading');
      grid.setAttribute('aria-busy', 'true');

      try {
        const manifest = await this.fetchManifest(currentUrl);
        if (version !== this.renderVersion || !grid.isConnected || currentUrl.href !== window.location.href) return;

        if (manifest.totalItems !== totalItems || manifest.products.length !== totalItems) {
          throw new Error('The collection changed while its stock-priority order was loading.');
        }
        if (new Set(manifest.products.map((product) => String(product.id))).size !== totalItems) {
          throw new Error('The collection contains duplicate product IDs.');
        }

        const availableProducts = manifest.products.filter((product) => product.available);
        const soldOutProducts = manifest.products.filter((product) => !product.available);
        if (balanceRecommended && (!window.DowntownRecommendedOrder || manifest.products.some(
          (product) => typeof product.vendor !== 'string' || typeof product.type !== 'string' || typeof product.title !== 'string'
        ))) {
          throw new Error('Recommended ordering metadata could not be loaded.');
        }
        const rankedProducts = balanceRecommended
          ? window.DowntownRecommendedOrder(manifest.products, prioritizeAvailable)
          : [...availableProducts, ...soldOutProducts];
        const startIndex = (currentPage - 1) * pageSize;
        const wantedProducts = rankedProducts.slice(startIndex, startIndex + pageSize);

        if (wantedProducts.length === 0) return;

        const currentIds = Array.from(grid.children, (card) => card.dataset.productId);
        const wantedIds = wantedProducts.map((product) => String(product.id));

        if (currentIds.join(',') === wantedIds.join(',')) {
          grid.dataset.recommendedReady = String(balanceRecommended);
          return;
        }

        const sourcePageById = new Map(
          manifest.products.map((product, index) => [String(product.id), Math.floor(index / pageSize) + 1])
        );
        const sourcePages = new Set(wantedIds.map((id) => sourcePageById.get(id)));
        const cardsById = new Map(Array.from(grid.children, (card) => [card.dataset.productId, card]));

        const pendingPages = Array.from(sourcePages).filter((page) => page !== currentPage);
        // Bound parallel section requests when a varied page draws from many source pages.
        await Promise.all(
          Array.from({ length: Math.min(4, pendingPages.length) }, async () => {
            while (pendingPages.length) {
              if (version !== this.renderVersion) return;
              const sourcePage = pendingPages.shift();
              const sourceGrid = await this.fetchProductGrid(currentUrl, sectionId, sourcePage);
              Array.from(sourceGrid.children).forEach((card) => {
                cardsById.set(card.dataset.productId, card);
              });
            }
          })
        );

        if (version !== this.renderVersion || !grid.isConnected || currentUrl.href !== window.location.href) return;

        const cards = wantedIds.map((id) => cardsById.get(id));
        if (cards.some((card) => !card)) {
          throw new Error('One or more product cards could not be loaded.');
        }

        const fragment = document.createDocumentFragment();
        cards.forEach((card, index) => {
          card.style.setProperty('--animation-order', index + 1);
          card.querySelectorAll('.scroll-trigger').forEach((element) => {
            element.classList.add('scroll-trigger--cancel');
          });

          if (index < 2) {
            card.querySelectorAll('img[loading="lazy"]').forEach((image) => image.removeAttribute('loading'));
          }

          fragment.appendChild(card);
        });

        grid.replaceChildren(fragment);
        grid.dataset.recommendedReady = String(balanceRecommended);
        grid.querySelectorAll('.scroll-trigger').forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
        });
        document.dispatchEvent(new CustomEvent('collection:stock-priority-rendered'));
      } catch (error) {
        console.warn('[Stock priority]', error);
      } finally {
        if (version === this.renderVersion) {
          collection?.classList.remove('loading', 'stock-priority-loading');
          grid.removeAttribute('aria-busy');
        }
      }
    }

    async fetchManifest(currentUrl) {
      const firstPage = await this.fetchManifestPage(currentUrl, 1);
      const remainingPages = await Promise.all(
        Array.from({ length: Math.max(0, firstPage.pages - 1) }, (_, index) =>
          this.fetchManifestPage(currentUrl, index + 2)
        )
      );

      return {
        totalItems: firstPage.totalItems,
        products: [firstPage, ...remainingPages].flatMap((page) => page.products),
      };
    }

    async fetchManifestPage(currentUrl, page) {
      const url = this.buildBaseUrl(currentUrl);
      url.searchParams.set('view', MANIFEST_VIEW);
      url.searchParams.set('page', String(page));

      const responseText = await this.fetchText(url);
      const manifest = JSON.parse(responseText);

      if (!Array.isArray(manifest.products) || !Number.isInteger(manifest.pages)) {
        throw new Error('The stock-priority manifest response was invalid.');
      }

      return manifest;
    }

    async fetchProductGrid(currentUrl, sectionId, page) {
      const url = this.buildBaseUrl(currentUrl);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('page', String(page));

      const responseText = await this.fetchText(url);
      const parsedDocument = new DOMParser().parseFromString(responseText, 'text/html');
      const sourceGrid = parsedDocument.getElementById('product-grid');

      if (!sourceGrid) throw new Error(`Product grid page ${page} was not found.`);
      return sourceGrid;
    }

    buildBaseUrl(currentUrl) {
      const url = new URL(currentUrl.pathname, currentUrl.origin);

      currentUrl.searchParams.forEach((value, key) => {
        if (key !== 'page' && key !== 'section_id' && key !== 'view') {
          url.searchParams.append(key, value);
        }
      });

      return url;
    }

    async fetchText(url) {
      const cacheKey = url.toString();

      if (!this.responseCache.has(cacheKey)) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 12000);
        const request = fetch(cacheKey, { credentials: 'same-origin', signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Request failed with status ${response.status}.`);
            return response.text();
          })
          .catch((error) => {
            this.responseCache.delete(cacheKey);
            throw error;
          })
          .finally(() => clearTimeout(timeout));

        this.responseCache.set(cacheKey, request);
      }

      return this.responseCache.get(cacheKey);
    }
  }

  const stockPriorityCollection = new StockPriorityCollection();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => stockPriorityCollection.init(), { once: true });
  } else {
    stockPriorityCollection.init();
  }
})();
